import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { Business as ProjectIcon } from '@mui/icons-material';
import { Employee } from '../../types';
import { formatDate } from '../../lib/formatDate';

interface EmployeeProjectHistoryProps {
  employee: Employee;
}

const EmployeeProjectHistory: React.FC<EmployeeProjectHistoryProps> = ({ employee }) => {
  const projects = employee.projects || [];

  if (projects.length === 0) {
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ProjectIcon />
          Project History (0)
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    No projects assigned yet
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  const getStatusChip = (projectEmployee: any) => {
    const now = new Date();
    const startDate = new Date(projectEmployee.startDate);
    const endDate = projectEmployee.endDate ? new Date(projectEmployee.endDate) : null;

    if (projectEmployee.shadow) {
      return <Chip label="Shadow" color="default" size="small" />;
    }

    if (endDate && endDate < now) {
      return <Chip label="Completed" color="success" size="small" />;
    } else if (startDate <= now) {
      return <Chip label="Active" color="primary" size="small" />;
    } else {
      return <Chip label="Upcoming" color="warning" size="small" />;
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <ProjectIcon />
        Project History ({projects.length})
      </Typography>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((projectEmployee, index) => (
              <TableRow key={`${projectEmployee.projectId}-${index}`}>
                <TableCell>
                  <Typography variant="body2">
                    {projectEmployee.project?.name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {projectEmployee.project.client?.name || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(projectEmployee.startDate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {projectEmployee.endDate ? formatDate(projectEmployee.endDate) : 'Ongoing'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {projectEmployee.endDate 
                      ? `${Math.ceil((new Date(projectEmployee.endDate).getTime() - new Date(projectEmployee.startDate).getTime()) / (1000 * 60 * 60 * 24))} days`
                      : `${Math.ceil((new Date().getTime() - new Date(projectEmployee.startDate).getTime()) / (1000 * 60 * 60 * 24))} days`
                    }
                  </Typography>
                </TableCell>
                <TableCell>
                  {getStatusChip(projectEmployee)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EmployeeProjectHistory;
